import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import { ClientSideRowModelModule } from 'ag-grid-community';
import { ModuleRegistry } from 'ag-grid-community';
import DataCollectionOverviewByModel from './DataCollectionOverviewByModel';
import { matchesYearFilter } from './utils/yearFilter';
import useGridLayout from './utils/useGridLayout';
import GridSaveLayoutButton from './components/GridSaveLayoutButton';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

// Register community module
ModuleRegistry.registerModules([ClientSideRowModelModule]);

const formatModelLabel = (meta, fallback) => {
  if (!meta) return fallback || 'Model';
  const make = meta.make ?? meta.Make;
  const modelName = meta.modelName ?? meta.ModelName;
  const streetName = meta.streetName ?? meta.StreetName;
  const year = meta.year ?? meta.Year;
  const region = meta.region ?? meta.Region;
  const parts = [make, modelName];
  if (streetName) parts.push(streetName);
  if (year !== null && year !== undefined) parts.push(year);
  const base = parts.filter(Boolean).join(' ');
  return region ? `${base} (${region})` : base;
};

export default function MakesModelsManager({ permissions, isDark, onOpenFicheWorkspace }) {
  const [activeTab, setActiveTab] = useState('Makes');
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [lookups, setLookups] = useState({ makes: [], regions: [], vehicleTypes: [] });
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [duplicateFormData, setDuplicateFormData] = useState({});
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copyFormData, setCopyFormData] = useState({});
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addFormData, setAddFormData] = useState({});
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addMakeModalVisible, setAddMakeModalVisible] = useState(false);
  const [addMakeFormData, setAddMakeFormData] = useState({});
  const [viewModelsModalVisible, setViewModelsModalVisible] = useState(false);
  const [selectedMake, setSelectedMake] = useState(null);
  const [manageColumnsVisible, setManageColumnsVisible] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const [modalColumnApi, setModalColumnApi] = useState(null);

  // Grid layout persistence (server-backed via useGridLayout hook)
  const modelsLayout = useGridLayout('makes-models-grid');

  const [modelQuickFilterInput, setModelQuickFilterInput] = useState('');
  const [modelQuickFilter, setModelQuickFilter] = useState('');
  const gridRef = useRef();
  const modalGridRef = useRef();
  const scrollPositionRef = useRef({ topRowIndex: null, verticalScroll: null });
  const inlineEditRevertingRef = useRef(false);
  const [errorPopupVisible, setErrorPopupVisible] = useState(false);
  const [partsListModalVisible, setPartsListModalVisible] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [partsData, setPartsData] = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const [pendingScrollKey, setPendingScrollKey] = useState(null);
  const [modelOverviewModal, setModelOverviewModal] = useState(null);
  const [sortOrderResequencing, setSortOrderResequencing] = useState(false);
  const canDataCollectionWrite = permissions?.['Data Collection/Reviewing'] === 'write';
  const canWrite = permissions['Makes & Models'] === 'write';

  // Initialize column visibility on mount
  useEffect(() => {
    const initialVisibility = {
      ModelKey: true,
      Make: true,
      Model: true,
      Year: true,
      ModelID: true,
      YearID: true,
      Region: true,
      EngineType: true,
      EngineCC: true,
      VehicleType: true,
      SortOrder: true,
      FitmentCount: true,
      LastUpdatedDate: false,
      Actions: true,
    };
    setColumnVisibility(initialVisibility);
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchMakes();
    fetchModels();
    fetchLookups();
  }, []);

  // Handle input changes for form fields
  const handleInputChange = useCallback((field, value, type = 'model') => {
    //console.log(`handleInputChange: field=${field}, value=${value}, type=${type}`);
    if (type === 'add') {
      setAddFormData(prev => ({ ...prev, [field]: value }));
    } else if (type === 'duplicate') {
      setDuplicateFormData(prev => ({ ...prev, [field]: value }));
    } else if (type === 'copy') {
      setCopyFormData(prev => ({ ...prev, [field]: value }));
    } else if (type === 'addMake') {
      setAddMakeFormData(prev => ({ ...prev, [field]: value }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  // Function to show error popup
  const showErrorPopup = (message) => {
    setError(message);
    setErrorPopupVisible(true);
  };

  // Function to close error popup
  const closeErrorPopup = () => {
    setError('');
    setErrorPopupVisible(false);
  };

  const openModelOverview = useCallback((model) => {
    if (!canDataCollectionWrite) {
      window.alert('You do not have permission to open the data collection overview.');
      return;
    }
    if (!model?.ModelKey) return;
    const parts = [model.Make, model.ModelName];
    if (model.Year != null && model.Year !== undefined) {
      parts.push(model.Year);
    }
    const baseLabel = parts.filter(Boolean).join(' ');
    const label = model.Region ? `${baseLabel} (${model.Region})` : baseLabel;
    setModelOverviewModal({
      modelKey: model.ModelKey,
      label,
      parentGroupKey: null,
      subGroupKey: null
    });
  }, [canDataCollectionWrite]);

  const closeModelOverview = useCallback(() => setModelOverviewModal(null), []);

  // Validation for uniqueness and field constraints
  const validateUniqueModel = async (data, editingId = null) => {
    if (!data.Make || !data.ModelName || !data.Year) {
      showErrorPopup('Make, ModelName, and Year are required');
      return false;
    }
    try {
      const validationData = {
        Make: data.Make,
        ModelName: data.ModelName,
        StreetName: data.StreetName === '' ? null : data.StreetName,
        Year: data.Year,
        ModelID: data.ModelID === '' ? null : data.ModelID,
        YearID: data.YearID === '' ? null : data.YearID,
        Region: data.Region,
        EngineType: data.EngineType === '' ? null : data.EngineType,
        EngineCC: data.EngineCC,
        VehicleType: data.VehicleType,
        ...(editingId && { ModelKey: editingId }),
      };
      const res = await axios.post('/api/models/validate-unique', validationData);
      return res.data.exists === false;
    } catch (err) {
      showErrorPopup(err.response?.data?.error || 'Validation failed');
      return false;
    }
  };

  const validateFields = (data) => {
    const year = parseInt(data.Year);
    if (data.Year && (isNaN(year) || year < 1886 || year > new Date().getFullYear() + 1)) {
      return 'Year must be a valid year between 1886 and ' + (new Date().getFullYear() + 1);
    }
    if (data.EngineCC && (isNaN(parseFloat(data.EngineCC)) || parseFloat(data.EngineCC) < 0)) {
      return 'EngineCC must be a positive number';
    }
    if (data.SortOrder && (isNaN(parseInt(data.SortOrder)) || parseInt(data.SortOrder) < 0)) {
      return 'SortOrder must be a non-negative number';
    }
    return '';
  };

  const validateMake = async (make) => {
    const trimmedMake = make?.trim();
    if (!trimmedMake) {
      showErrorPopup('Make is required');
      return false;
    }
    try {
      const res = await axios.post('/api/makes/validate-unique', { Make: trimmedMake });
      return res.data.exists === false;
    } catch (err) {
      showErrorPopup(err.response?.data?.error || 'Make validation failed');
      return false;
    }
  };

  const fetchMakes = async () => {
    try {
      const res = await axios.get('/api/makes');
      setMakes(res.data);
      setLookups(prev => ({ ...prev, makes: res.data.sort((a, b) => a.Make.localeCompare(b.Make)) }));
    } catch (err) {
      showErrorPopup(`Failed to fetch makes: ${err.response?.data?.error || err.message}`);
    }
  };

  const fetchModels = async (preserveScroll = false) => {
    try {
      if (preserveScroll && gridApi) {
        const topRowIndex = typeof gridApi.getFirstDisplayedRowIndex === 'function'
          ? gridApi.getFirstDisplayedRowIndex()
          : null;
        const pixelRange = typeof gridApi.getVerticalPixelRange === 'function'
          ? gridApi.getVerticalPixelRange()
          : null;
        scrollPositionRef.current = {
          topRowIndex: typeof topRowIndex === 'number' && topRowIndex >= 0 ? topRowIndex : null,
          verticalScroll: pixelRange ? pixelRange.top : null,
        };
      } else {
        scrollPositionRef.current = { topRowIndex: null, verticalScroll: null };
      }
      setLoading(true);
      const res = await axios.get('/api/models');
      setModels(res.data);
      return res.data;
    } catch (err) {
      showErrorPopup(`Failed to fetch models: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
      if (
        preserveScroll &&
        gridApi &&
        scrollPositionRef.current.topRowIndex !== null &&
        typeof gridApi.ensureIndexVisible === 'function'
      ) {
        const restoreScroll = () => {
          gridApi.ensureIndexVisible(scrollPositionRef.current.topRowIndex, 'top');
          if (
            typeof scrollPositionRef.current.verticalScroll === 'number' &&
            typeof gridApi.setVerticalScrollPosition === 'function'
          ) {
            gridApi.setVerticalScrollPosition(scrollPositionRef.current.verticalScroll);
          }
        };
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(restoreScroll);
        } else {
          setTimeout(restoreScroll, 0);
        }
      }
    }
  };

  const fetchLookups = async () => {
    try {
      const [regionRes, vtRes] = await Promise.all([
        axios.get('/api/regions'),
        axios.get('/api/vehicle-types'),
      ]);
      const sortedRegions = regionRes.data.sort((a, b) => {
        const priority = ['UK', 'Global', 'Europe', 'International'];
        const aIndex = priority.indexOf(a.RegionName);
        const bIndex = priority.indexOf(b.RegionName);
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.RegionName.localeCompare(b.RegionName);
      });
      const sortedVehicleTypes = vtRes.data.sort((a, b) => a.VehicleTypeName.localeCompare(b.VehicleTypeName));
      setLookups(prev => ({
        ...prev,
        regions: sortedRegions,
        vehicleTypes: sortedVehicleTypes,
      }));
    } catch (err) {
      showErrorPopup(`Failed to fetch lookups: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isAddingMake = activeTab === 'Makes' && addMakeModalVisible;
    const data = activeTab === 'Makes'
      ? (isAddingMake ? addMakeFormData : formData)
      : (editModalVisible ? formData : addFormData);

    if (activeTab !== 'Makes' && (!data.Make || !data.ModelName || !data.Year)) {
      showErrorPopup('Make, ModelName, and Year are required');
      return;
    }

    if (activeTab === 'Makes') {
      const makeValue = (isAddingMake ? addMakeFormData.Make : formData.Make) || '';
      if (!makeValue.trim()) {
        showErrorPopup('Make is required');
        return;
      }
    }

    setLoading(true);
    setError('');
    setErrorPopupVisible(false);
    try {
      const fieldError = validateFields(data);
      if (fieldError) {
        showErrorPopup(fieldError);
        return;
      }
      if (activeTab === 'Makes') {
        const targetMake = (isAddingMake ? addMakeFormData.Make : formData.Make)?.trim();
        if (isAddingMake) {
          const isUnique = await validateMake(targetMake);
          if (!isUnique) {
            showErrorPopup('Make already exists');
            return;
          }
          await axios.post('/api/makes', { Make: targetMake, LastUpdatedDate: new Date().toISOString() });
        } else if (editing) {
          await axios.patch(`/api/makes/${editing.id}`, { Make: targetMake, LastUpdatedDate: new Date().toISOString() });
        } else {
          await axios.post('/api/makes', { Make: targetMake, LastUpdatedDate: new Date().toISOString() });
        }
      } else {
        if (editing && editModalVisible) {
          await axios.patch(`/api/models/${editing.id}`, { ...formData, LastUpdatedDate: new Date().toISOString() });
          if (editing?.id) {
            setPendingScrollKey(editing.id.toString());
          }
        } else {
          const isUnique = await validateUniqueModel(addFormData);
          if (!isUnique) {
            showErrorPopup('Model with these details already exists');
            return;
          }
          const { ModelKey, ...payload } = addFormData;
          const res = await axios.post('/api/models', { ...payload, LastUpdatedDate: new Date().toISOString() });
          if (res?.data?.ModelKey != null) {
            setPendingScrollKey(res.data.ModelKey.toString());
          }
        }
      }
      if (activeTab === 'Makes') fetchMakes();
      else await fetchModels();
      setEditing(null);
      setFormData({});
      setAddModalVisible(false);
      setAddFormData({});
      setEditModalVisible(false);
      setAddMakeModalVisible(false);
      setAddMakeFormData({});
    } catch (err) {
      showErrorPopup(err.response?.data?.error || err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setAddFormData({});
    setAddModalVisible(true);
  };

  const openAddMakeModal = () => {
    setAddMakeFormData({});
    setAddMakeModalVisible(true);
  };

  const handleEdit = (item, type) => {
    setEditing({ id: item.ModelKey || item.MakeKey, type });
    setFormData({
      Make: item.Make || '',
      ModelName: item.ModelName || '',
      StreetName: item.StreetName || '',
      Year: item.Year || '',
      SortOrder: item.SortOrder || '',
      ModelID: item.ModelID || '',
      YearID: item.YearID || '',
      Region: item.Region || '',
      EngineType: item.EngineType || '',
      EngineCC: item.EngineCC ?? '',
      VehicleType: item.VehicleType || '',
    });
    if (type === 'model') {
      setEditModalVisible(true);
    }
  };

  const handleDelete = async (id, type, itemName = '') => {
    if (!window.confirm(`Delete this ${type}? ${itemName ? `(${itemName})` : ''}`)) return;
    setLoading(true);
    try {
      const endpoint = type === 'make' ? `/api/makes/${id}` : `/api/models/${id}`;
      await axios.delete(endpoint);
      if (type === 'make') fetchMakes();
      else fetchModels(true);
    } catch (err) {
      showErrorPopup(`Delete failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyModel = useCallback((rowData) => {
    const newData = {
      Make: rowData.Make || '',
      ModelName: rowData.ModelName || '',
      StreetName: rowData.StreetName || '',
      Year: rowData.Year || '',
      SortOrder: rowData.SortOrder || '',
      ModelID: rowData.ModelID || '',
      YearID: rowData.YearID || '',
      Region: rowData.Region || '',
      EngineType: rowData.EngineType || '',
      EngineCC: rowData.EngineCC ?? '',
      VehicleType: rowData.VehicleType || '',
    };
    setCopyFormData(newData);
    setCopyModalVisible(true);
  }, []);

  const handleSaveCopy = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorPopupVisible(false);
    try {
      const fieldError = validateFields(copyFormData);
      if (fieldError) {
        showErrorPopup(fieldError);
        return;
      }
      const isUnique = await validateUniqueModel(copyFormData);
      if (!isUnique) {
        showErrorPopup('Model with these details already exists');
        return;
      }
      const { ModelKey, ...payload } = copyFormData;
      const res = await axios.post('/api/models', { ...payload, LastUpdatedDate: new Date().toISOString() });
      if (res?.data?.ModelKey != null) {
        setPendingScrollKey(res.data.ModelKey.toString());
      }
      await fetchModels();
      setCopyModalVisible(false);
      setCopyFormData({});
    } catch (err) {
      showErrorPopup(`Save copy failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateYear = useCallback((rowData, yearDelta) => {
    const newData = {
      Make: rowData.Make || '',
      ModelName: rowData.ModelName || '',
      StreetName: rowData.StreetName || '',
      Year: (parseInt(rowData.Year) || 0) + yearDelta,
      SortOrder: (parseInt(rowData.SortOrder) || 0) + yearDelta,
      ModelID: rowData.ModelID || '',
      YearID: rowData.YearID || '',
      Region: rowData.Region || '',
      EngineType: rowData.EngineType || '',
      EngineCC: rowData.EngineCC ?? '',
      VehicleType: rowData.VehicleType || '',
    };
    setDuplicateFormData(newData);
    setDuplicateModalVisible(true);
  }, []);

  const handleSaveDuplicate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorPopupVisible(false);
    try {
      const fieldError = validateFields(duplicateFormData);
      if (fieldError) {
        showErrorPopup(fieldError);
        return;
      }
      const isUnique = await validateUniqueModel(duplicateFormData);
      if (!isUnique) {
        showErrorPopup('Model with these details already exists');
        return;
      }
      const { ModelKey, ...payload } = duplicateFormData;
      const res = await axios.post('/api/models', { ...payload, LastUpdatedDate: new Date().toISOString() });
      if (res?.data?.ModelKey != null) {
        setPendingScrollKey(res.data.ModelKey.toString());
      }
      await fetchModels();
      setDuplicateModalVisible(false);
      setDuplicateFormData({});
    } catch (err) {
      showErrorPopup(`Save duplicate failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePartsList = useCallback((rowData) => {
  setSelectedModel(rowData);
  setPartsListModalVisible(true);
  fetchParts(rowData.ModelKey);
}, []);

const fetchParts = async (modelKey) => {
  console.log('Fetching parts for modelKey:', modelKey);
  setLoadingParts(true);
  try {
    const res = await axios.get(`/api/fitments/${modelKey}`);
    console.log('API response:', res.data);
    setPartsData(res.data);
  } catch (err) {
    console.error('Fetch parts error:', err);
    showErrorPopup(err.response?.data?.error || 'Failed to fetch parts list');
  } finally {
    setLoadingParts(false);
  }
};

const handlePrint = () => {
  window.print();
};

  const handleViewModels = (make) => {
    setSelectedMake(make);
    setViewModelsModalVisible(true);
  };

  const handleResequenceSortOrder = useCallback(async () => {
    if (sortOrderResequencing) return;
    const confirmed = window.confirm('This will renumber all model SortOrder values in their current order using steps of 100 (LastUpdatedDate will not change). Continue?');
    if (!confirmed) return;
    setSortOrderResequencing(true);
    try {
      await axios.post('/api/models/resequence-sort-order');
      await fetchModels(true);
      window.alert('Model sort order re-spaced in 100-point increments.');
    } catch (err) {
      showErrorPopup(err.response?.data?.error || 'Failed to resequence sort order');
    } finally {
      setSortOrderResequencing(false);
    }
  }, [sortOrderResequencing, fetchModels]);

  const getModelDisplay = useCallback((row) => {
    if (!row) return '—';
    const toTrimmed = (value) => {
      if (typeof value === 'string') return value.trim();
      if (value == null) return '';
      return String(value).trim();
    };
    const model = toTrimmed(row.ModelName);
    const street = toTrimmed(row.StreetName);
    if (model && street) return `${model} ${street}`;
    if (model) return model;
    if (street) return street;
    return '—';
  }, []);

  const toggleColumn = (field) => {
    setColumnVisibility(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const parseEditableNumber = useCallback((params) => {
    const raw = String(params.newValue ?? '').trim();
    if (!raw) return '';
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : params.newValue;
  }, []);

  const buildInlineModelPayload = useCallback((row) => ({
    Make: String(row?.Make ?? '').trim(),
    ModelName: String(row?.ModelName ?? '').trim(),
    Year: row?.Year ?? '',
    SortOrder: row?.SortOrder ?? '',
    ModelID: row?.ModelID ?? '',
    YearID: row?.YearID ?? '',
    Region: String(row?.Region ?? '').trim(),
    EngineType: row?.EngineType ?? '',
    EngineCC: row?.EngineCC ?? '',
    StreetName: row?.StreetName ?? '',
    VehicleType: String(row?.VehicleType ?? '').trim(),
    LastUpdatedDate: new Date().toISOString(),
  }), []);

  const rollbackInlineEdit = useCallback((params) => {
    const field = params?.colDef?.field;
    const modelKey = params?.data?.ModelKey;
    if (!field) return;

    inlineEditRevertingRef.current = true;
    params.node?.setDataValue(field, params.oldValue);

    if (modelKey != null) {
      setModels(prev => prev.map((model) => (
        model.ModelKey === modelKey
          ? { ...model, [field]: params.oldValue }
          : model
      )));
    }
  }, []);

  const handleInlineModelCellValueChanged = useCallback(async (params) => {
    if (!canWrite) return;
    if (inlineEditRevertingRef.current) {
      inlineEditRevertingRef.current = false;
      return;
    }

    const field = params?.colDef?.field;
    const modelKey = params?.data?.ModelKey;
    if (!field || modelKey == null) return;
    if (field === 'ModelKey' || field === 'FitmentCount' || field === 'LastUpdatedDate') return;

    const oldVal = params.oldValue;
    const newVal = params.newValue;
    if (String(oldVal ?? '') === String(newVal ?? '')) return;

    const payload = buildInlineModelPayload(params.data);

    if (!payload.Make || !payload.ModelName || payload.Year === '' || payload.SortOrder === '' || !payload.Region || !payload.VehicleType || payload.EngineCC === '') {
      showErrorPopup('Make, ModelName, Year, SortOrder, Region, VehicleType, and EngineCC are required');
      rollbackInlineEdit(params);
      return;
    }

    const fieldError = validateFields(payload);
    if (fieldError) {
      showErrorPopup(fieldError);
      rollbackInlineEdit(params);
      return;
    }

    try {
      await axios.patch(`/api/models/${modelKey}`, payload);

      if (params.node && typeof params.node.setDataValue === 'function') {
        params.node.setDataValue('LastUpdatedDate', payload.LastUpdatedDate);
      }

      setModels(prev => prev.map((model) => (
        model.ModelKey === modelKey
          ? { ...model, ...payload }
          : model
      )));
    } catch (err) {
      showErrorPopup(err.response?.data?.error || 'Inline update failed');
      rollbackInlineEdit(params);
    }
  }, [buildInlineModelPayload, canWrite, rollbackInlineEdit, validateFields]);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Model Key',
      field: 'ModelKey',
      filter: 'agTextColumnFilter',
      sortable: true,
      resizable: true,
      hide: !columnVisibility.ModelKey,
      width: 90,
      minWidth: 80,
      maxWidth: 110,
      flex: 0,
      suppressSizeToFit: true,
      editable: false,
      valueGetter: (params) => (params.data?.ModelKey ?? '').toString()
    },
    {
      field: 'Make',
      filter: true,
      sortable: true,
      resizable: true,
      hide: !columnVisibility.Make,
      width: 120,
      minWidth: 100,
      maxWidth: 160,
      flex: 0,
      suppressSizeToFit: true,
      editable: canWrite,
    },
    {
      headerName: 'Model',
      field: 'ModelName',
      colId: 'Model',
      valueFormatter: params => getModelDisplay(params.data),
      filterValueGetter: params => getModelDisplay(params.data),
      tooltipValueGetter: params => getModelDisplay(params.data),
      filter: 'agTextColumnFilter',
      sortable: true,
      resizable: true,
      hide: !columnVisibility.Model,
      minWidth: 240,
      flex: 2,
      editable: canWrite,
    },
    {
      field: 'Year',
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: ({ filterText, value }) => {
          const query = (filterText || '').trim();
          if (!query) return true;
          const display = value == null ? '' : String(value);
          const numeric = Number(display);
          const yearValues = Number.isFinite(numeric) ? [numeric] : [];
          return matchesYearFilter(yearValues, display, query);
        }
      },
      sortable: true,
      resizable: true,
      hide: !columnVisibility.Year,
      width: 80,
      minWidth: 70,
      maxWidth: 90,
      flex: 0,
      suppressSizeToFit: true,
      editable: canWrite,
      valueParser: parseEditableNumber,
    },
    {
      field: 'ModelID',
      filter: true,
      sortable: true,
      resizable: true,
      hide: !columnVisibility.ModelID,
      width: 95,
      minWidth: 85,
      maxWidth: 110,
      flex: 0,
      suppressSizeToFit: true,
      editable: canWrite,
    },
    {
      field: 'YearID',
      filter: true,
      sortable: true,
      resizable: true,
      hide: !columnVisibility.YearID,
      width: 85,
      minWidth: 75,
      maxWidth: 100,
      flex: 0,
      suppressSizeToFit: true,
      editable: canWrite,
    },
    {
      field: 'Region',
      filter: true,
      sortable: true,
      resizable: true,
      hide: !columnVisibility.Region,
      width: 95,
      minWidth: 85,
      maxWidth: 120,
      flex: 0,
      suppressSizeToFit: true,
      editable: canWrite,
    },
    {
      field: 'EngineType',
      filter: true,
      sortable: true,
      resizable: true,
      hide: !columnVisibility.EngineType,
      minWidth: 170,
      flex: 1,
      editable: canWrite,
    },
    {
      field: 'EngineCC',
      filter: 'agNumberColumnFilter',
      sortable: true,
      resizable: true,
      hide: !columnVisibility.EngineCC,
      width: 95,
      minWidth: 85,
      maxWidth: 110,
      flex: 0,
      suppressSizeToFit: true,
      editable: canWrite,
      valueParser: parseEditableNumber,
    },
    {
      field: 'VehicleType',
      filter: true,
      sortable: true,
      resizable: true,
      hide: !columnVisibility.VehicleType,
      width: 110,
      minWidth: 95,
      maxWidth: 130,
      flex: 0,
      suppressSizeToFit: true,
      editable: canWrite,
    },
    {
      field: 'SortOrder',
      filter: 'agNumberColumnFilter',
      sortable: true,
      resizable: true,
      hide: !columnVisibility.SortOrder,
      width: 110,
      minWidth: 95,
      maxWidth: 130,
      flex: 0,
      suppressSizeToFit: true,
      editable: canWrite,
      valueParser: parseEditableNumber,
    },
    {
      headerName: 'Fitments',
      field: 'FitmentCount',
      filter: 'agNumberColumnFilter',
      sortable: true,
      resizable: true,
      hide: !columnVisibility.FitmentCount,
      width: 100,
      minWidth: 80,
      maxWidth: 120,
      flex: 0,
      suppressSizeToFit: true,
      editable: false,
    },
    { field: 'LastUpdatedDate', filter: 'agDateColumnFilter', sortable: true, resizable: true, hide: !columnVisibility.LastUpdatedDate, editable: false },
{
  headerName: 'Actions',
  cellRenderer: (params) => {
    const renderDataCollectionButton = (styleOverrides = {}) => {
      if (!canDataCollectionWrite) return null;
      return (
        <button
          onClick={() => openModelOverview(params.data)}
          style={{ padding: '2px 6px', fontSize: '12px', ...styleOverrides }}
        >
          Data Collection
        </button>
      );
    };

    return (
      <div style={{ whiteSpace: 'nowrap' }}>
        {permissions['Makes & Models'] === 'write' && (
          <>
            <button onClick={() => handleEdit(params.data, 'model')} style={{ marginRight: 5, padding: '2px 6px', fontSize: '12px' }}>Edit</button>
            <button onClick={() => handleDelete(params.data.ModelKey, 'model', `${params.data.ModelName} ${params.data.Year}`)} style={{ marginRight: 5, padding: '2px 6px', fontSize: '12px', backgroundColor: 'red', color: 'white' }}>Delete</button>
            <button onClick={() => handleCopyModel(params.data)} style={{ marginRight: 5, padding: '2px 6px', fontSize: '12px' }}>Copy</button>
            {renderDataCollectionButton({ marginRight: 5 })}
            <button onClick={() => handleDuplicateYear(params.data, 1)} style={{ marginRight: 5, padding: '2px 6px', fontSize: '12px' }}>+1 Year</button>
            <button onClick={() => handleDuplicateYear(params.data, -1)} style={{ marginRight: 5, padding: '2px 6px', fontSize: '12px' }}>-1 Year</button>
            <button onClick={() => handlePartsList(params.data)} style={{ padding: '2px 6px', fontSize: '12px' }}>Parts List</button>
            {onOpenFicheWorkspace && <button onClick={() => onOpenFicheWorkspace(params.data.ModelKey)} style={{ marginLeft: 5, padding: '2px 6px', fontSize: '12px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Fiches</button>}
          </>
        )}
  {permissions['Makes & Models'] !== 'write' && renderDataCollectionButton({ marginLeft: 5 })}
      </div>
    );
  },
  width: 530,
  minWidth: 490,
  flex: 0,
  suppressSizeToFit: true,
  resizable: true,
  sortable: false,
  filter: false,
  editable: false,
  hide: !columnVisibility.Actions,
},
  ], [permissions, columnVisibility, canDataCollectionWrite, getModelDisplay, onOpenFicheWorkspace, canWrite, parseEditableNumber]);

  const getRowId = useCallback((params) => (
    params.data?.ModelKey != null ? params.data.ModelKey.toString() : undefined
  ), []);

  const normalizeSearchText = useCallback((value) => {
    const text = String(value ?? '').toLowerCase();
    return {
      raw: text,
      alnum: text.replace(/[^a-z0-9]+/g, '')
    };
  }, []);

  const parseNullableInt = useCallback((value) => {
    const n = Number.parseInt(String(value ?? ''), 10);
    return Number.isFinite(n) ? n : null;
  }, []);

  const isPlausibleYear = useCallback((year) => {
    const n = typeof year === 'number' ? year : parseNullableInt(year);
    if (n == null) return false;
    const maxYear = new Date().getFullYear() + 1;
    return n >= 1886 && n <= maxYear;
  }, [parseNullableInt]);

  const expandTwoDigitYear = useCallback((yy) => {
    const n = parseNullableInt(yy);
    if (n === null) return null;
    if (n < 0 || n > 99) return null;
    // Heuristic pivot: 00-29 => 2000-2029, 30-99 => 1930-1999
    return n <= 29 ? 2000 + n : 1900 + n;
  }, [parseNullableInt]);

  const parseYearToken = useCallback(
    (token) => {
      const raw = String(token ?? '').trim();
      if (!raw) return null;
      if (!/^\d{1,4}$/.test(raw)) return null;
      if (raw.length === 4) {
        const n = parseNullableInt(raw);
        return isPlausibleYear(n) ? n : null;
      }
      if (raw.length === 2) {
        const expanded = expandTwoDigitYear(raw);
        return isPlausibleYear(expanded) ? expanded : null;
      }
      return null;
    },
    [expandTwoDigitYear, isPlausibleYear, parseNullableInt]
  );

  const parseYearRangeToken = useCallback(
    (token) => {
      const raw = String(token ?? '').trim();
      if (!raw) return null;
      // Allow hyphen or en-dash.
      // Allow open-ended ranges like "2017-" or "-2017".
      const m = raw.match(/^\s*(\d{0,4})\s*[-–]\s*(\d{0,4})\s*$/);
      if (!m) return null;
      const leftRaw = m[1];
      const rightRaw = m[2];

      if (!leftRaw && !rightRaw) return null;

      const leftIs4 = leftRaw.length === 4;
      const rightIs4 = rightRaw.length === 4;
      const leftIs2 = leftRaw.length === 2;
      const rightIs2 = rightRaw.length === 2;

      let startYear = leftRaw ? parseYearToken(leftRaw) : null;
      let endYear = rightRaw ? parseYearToken(rightRaw) : null;

      // Handle mixed-width ranges like 1993-04 or 93-2004.
      if (startYear != null && endYear != null && leftIs4 && rightIs2) {
        const yy = parseNullableInt(rightRaw);
        if (yy == null) return null;
        const baseCentury = Math.floor(startYear / 100) * 100;
        const startYY = startYear % 100;
        endYear = (yy < startYY ? baseCentury + 100 : baseCentury) + yy;
      } else if (startYear != null && endYear != null && rightIs4 && leftIs2) {
        const yy = parseNullableInt(leftRaw);
        if (yy == null) return null;
        const baseCentury = Math.floor(endYear / 100) * 100;
        startYear = baseCentury + yy;
        if (startYear > endYear) startYear -= 100;
      }

      if (startYear == null && endYear == null) return null;
      if (startYear != null && endYear != null && endYear < startYear) return null;

      return { startYear, endYear };
    },
    [parseNullableInt, parseYearToken]
  );

  const parsedModelSearch = useMemo(() => {
    const q = modelQuickFilter.trim();
    if (!q) return null;

    const tokens = q
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);

    if (!tokens.length) return null;

    const tokenInfo = tokens.map((token) => {
      const norm = normalizeSearchText(token);
      const yearExact = parseYearToken(token);
      return {
        token,
        norm,
        isDigits: /^\d+$/.test(token),
        isTwoDigitNumber: /^\d{2}$/.test(token),
        yearRange: parseYearRangeToken(token),
        yearExact,
        isStrictYearToken: /^\d{4}$/.test(token) && yearExact != null,
        isAmbiguousTwoDigitYearToken: /^\d{2}$/.test(token) && yearExact != null,
      };
    });

    const alnums = tokenInfo
      .map((t) => t.norm.alnum)
      .filter((t) => typeof t === 'string' && t.length > 0);

    const hasRepeats = (() => {
      const seen = new Set();
      for (const a of alnums) {
        if (seen.has(a)) return true;
        seen.add(a);
      }
      return false;
    })();

    const hasOverlap = (() => {
      for (let i = 0; i < alnums.length; i += 1) {
        for (let j = 0; j < alnums.length; j += 1) {
          if (i === j) continue;
          const a = alnums[i];
          const b = alnums[j];
          if (!a || !b) continue;
          if (a.length <= b.length) continue;
          if (a.includes(b)) return true;
        }
      }
      return false;
    })();

    return {
      tokenInfo,
      requireNonOverlappingMatches: hasRepeats || hasOverlap,
    };
  }, [modelQuickFilter, normalizeSearchText, parseYearRangeToken, parseYearToken]);

  const buildModelSearchRowData = useCallback((row) => {
    const d = row || {};
    const rowYear = parseNullableInt(d.Year);
    const modelKeyExact = d.ModelKey != null ? String(d.ModelKey).trim() : '';
    const modelName = typeof d.ModelName === 'string' ? d.ModelName : (d.ModelName == null ? '' : String(d.ModelName));
    const streetName = typeof d.StreetName === 'string' ? d.StreetName : (d.StreetName == null ? '' : String(d.StreetName));
    const modelDisplay = `${modelName} ${streetName}`.trim();

    const haystackRaw = [
      d.Make,
      modelDisplay,
      d.Year,
      d.ModelID,
      d.YearID,
      d.Region,
      d.EngineType,
      d.EngineCC,
      d.VehicleType
    ]
      .filter((v) => v !== null && v !== undefined)
      .map((v) => String(v))
      .join(' | ');

    return {
      rowYear,
      modelKeyExact,
      hay: normalizeSearchText(haystackRaw)
    };
  }, [normalizeSearchText, parseNullableInt]);

  const modelSearchIndexByKey = useMemo(() => {
    const index = new Map();
    for (const row of models) {
      if (row?.ModelKey == null) continue;
      index.set(String(row.ModelKey), buildModelSearchRowData(row));
    }
    return index;
  }, [models, buildModelSearchRowData]);

  const isExternalModelSearchPresent = useCallback(() => {
    return parsedModelSearch != null;
  }, [parsedModelSearch]);

  const doesExternalModelSearchPass = useCallback(
    (node) => {
      if (!parsedModelSearch) return true;

      const d = node?.data || {};
      const modelKey = d.ModelKey != null ? String(d.ModelKey) : '';
      const precomputed = modelKey ? modelSearchIndexByKey.get(modelKey) : null;
      const rowSearchData = precomputed || buildModelSearchRowData(d);
      const { rowYear, modelKeyExact, hay } = rowSearchData;
      const { tokenInfo, requireNonOverlappingMatches } = parsedModelSearch;

      // Always enforce year ranges as numeric comparisons (not text matching).
      for (const t of tokenInfo) {
        if (t.yearRange) {
          if (rowYear == null) return false;
          const { startYear, endYear } = t.yearRange;
          if (startYear != null && rowYear < startYear) return false;
          if (endYear != null && rowYear > endYear) return false;
        } else if (t.isStrictYearToken) {
          // 4-digit years behave as strict year filters.
          if (rowYear == null) return false;
          if (rowYear !== t.yearExact) return false;
        }
      }

      if (!requireNonOverlappingMatches) {
        // Fast path: simple substring match across allowed fields.
        return tokenInfo.every((t) => {
          // Ranges and strict 4-digit years are already enforced above.
          if (t.yearRange || t.isStrictYearToken) return true;

          // For ambiguous 2-digit numbers (e.g., "90"), treat as year OR text.
          // This prevents "LT-Z 90" from being interpreted as a strict year=1990 filter.
          if (t.isAmbiguousTwoDigitYearToken) {
            if (rowYear != null && rowYear === t.yearExact) return true;
            // fall through to normal text matching, so "90" must exist in text somewhere.
          }

          // ModelKey should only match on exact equality (no partial substring matches).
          // This prevents queries like "400 suzuki" from matching ModelKey "34005".
          if (t.isDigits) {
            // If this numeric token is a strict year token, don't treat it as a ModelKey search.
            if (!t.isStrictYearToken && modelKeyExact && modelKeyExact === t.token) return true;
          }

          if (!t.norm.raw.trim()) return true;
          if (hay.raw.includes(t.norm.raw)) return true;
          if (t.norm.alnum && hay.alnum.includes(t.norm.alnum)) return true;
          return false;
        });
      }

      // Slow path (still fast in practice): require non-overlapping matches for tokens,
      // so queries like "lt-f ... f" require a second "f" beyond the one in "lt-f".
      let remaining = hay.alnum;
      const consumeList = tokenInfo
        .filter((t) => !(t.yearRange || t.yearExact != null))
        .slice()
        .sort((a, b) => (b.norm.alnum.length || 0) - (a.norm.alnum.length || 0));

      // Add ambiguous 2-digit numeric tokens to the consume list too (unless they matched year already).
      for (const t of tokenInfo) {
        if (!t.isTwoDigitNumber || t.yearExact == null) continue;
        if (rowYear != null && rowYear === t.yearExact) continue;
        consumeList.push(t);
      }

      consumeList.sort((a, b) => (b.norm.alnum.length || 0) - (a.norm.alnum.length || 0));

      for (const t of consumeList) {
        if (t.isDigits && modelKeyExact && modelKeyExact === t.token) {
          continue;
        }

        const needle = t.norm.alnum;
        if (!needle) continue;

        const idx = remaining.indexOf(needle);
        if (idx === -1) return false;
        remaining = `${remaining.slice(0, idx)}${' '.repeat(needle.length)}${remaining.slice(idx + needle.length)}`;
      }

      return true;
    },
    [buildModelSearchRowData, modelSearchIndexByKey, parsedModelSearch]
  );

  const commonGridProps = useMemo(() => ({
    columnDefs,
    rowModelType: 'clientSide',
    defaultColDef: {
      editable: false,
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 50,
    },
    // Search should only consider a specific subset of model fields.
    // Use an external filter (instead of AG Grid quick filter) so we can explicitly
    // exclude fields like SortOrder.
    isExternalFilterPresent: isExternalModelSearchPresent,
    doesExternalFilterPass: doesExternalModelSearchPass,
    suppressNoRowsOverlay: true,
    animateRows: false,
    theme: 'legacy',
    getRowId,
    singleClickEdit: false,
    stopEditingWhenCellsLoseFocus: true,
    onCellValueChanged: handleInlineModelCellValueChanged,
    rowSelection: { mode: 'singleRow' },
  }), [columnDefs, getRowId, isDark, isExternalModelSearchPresent, doesExternalModelSearchPass, handleInlineModelCellValueChanged]);

  const sizeColumnsToFitSafe = useCallback((api) => {
    if (!api) return;
    if (typeof api.sizeColumnsToFit !== 'function') return;
    try {
      api.sizeColumnsToFit();
    } catch {
      // ignore
    }
  }, []);

  const applyExternalModelSearch = useCallback((api) => {
    if (!api) return;
    try {
      if (typeof api.onFilterChanged === 'function') api.onFilterChanged();
      if (typeof api.refreshClientSideRowModel === 'function') api.refreshClientSideRowModel('filter');
    } catch {
      // ignore
    }
  }, []);

  const onGridReadyMain = useCallback((params) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
    modelsLayout.onGridReady(params);
    applyExternalModelSearch(params.api);
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => sizeColumnsToFitSafe(params.api));
    } else {
      setTimeout(() => sizeColumnsToFitSafe(params.api), 0);
    }
  }, [applyExternalModelSearch, modelsLayout, sizeColumnsToFitSafe]);

  useEffect(() => {
    if (activeTab !== 'Models' || !gridApi) return;
    const onResize = () => sizeColumnsToFitSafe(gridApi);
    window.addEventListener('resize', onResize);
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(onResize);
    } else {
      setTimeout(onResize, 0);
    }
    return () => window.removeEventListener('resize', onResize);
  }, [activeTab, gridApi, sizeColumnsToFitSafe]);

  useEffect(() => {
    if (activeTab !== 'Models') return;
    const handle = setTimeout(() => {
      setModelQuickFilter(modelQuickFilterInput);
    }, 350);
    return () => clearTimeout(handle);
  }, [activeTab, modelQuickFilterInput]);

  useEffect(() => {
    if (activeTab !== 'Models' || !gridApi) return;
    applyExternalModelSearch(gridApi);
  }, [activeTab, gridApi, modelQuickFilter, applyExternalModelSearch]);

  useEffect(() => {
    if (activeTab !== 'Models' || !gridApi) return;
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => sizeColumnsToFitSafe(gridApi));
    } else {
      setTimeout(() => sizeColumnsToFitSafe(gridApi), 0);
    }
  }, [activeTab, gridApi, columnVisibility, sizeColumnsToFitSafe]);

  const clearGridFilters = useCallback(() => {
    if (!gridApi) return;
    gridApi.setFilterModel(null);
    setModelQuickFilterInput('');
    setModelQuickFilter('');
    applyExternalModelSearch(gridApi);
  }, [gridApi, applyExternalModelSearch]);

  const onChangeModelQuickFilter = useCallback((value) => {
    setModelQuickFilterInput(value);
  }, []);

  const onGridReadyModal = useCallback((params) => {
    setModalColumnApi(params.columnApi);
  }, []);

  const mainRowData = useMemo(() => models, [models]);
  const modalRowData = useMemo(() => (
    selectedMake
      ? models.filter(model => model.Make === selectedMake)
      : []
  ), [models, selectedMake]);

  useEffect(() => {
    if (activeTab !== 'Models' || !gridApi || !pendingScrollKey) return;
    const targetKey = pendingScrollKey.toString();
    let rowNode = gridApi.getRowNode(targetKey);
    if (!rowNode) {
      gridApi.forEachNode(node => {
        if (!rowNode && node.data?.ModelKey != null && node.data.ModelKey.toString() === targetKey) {
          rowNode = node;
        }
      });
    }
    if (!rowNode) return;

    gridApi.ensureNodeVisible(rowNode, 'middle');
    const displayedColumns = columnApi?.getAllDisplayedColumns();
    if (displayedColumns && displayedColumns.length > 0) {
      gridApi.setFocusedCell(rowNode.rowIndex, displayedColumns[0].getColId());
    }
    rowNode.setSelected(true);
    setPendingScrollKey(null);
  }, [models, pendingScrollKey, gridApi, columnApi, activeTab]);

  const toolbarButtonStyle = useMemo(
    () => ({
      padding: '0.4rem 1rem',
      borderRadius: '4px',
      minHeight: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1
    }),
    []
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <div
        style={{
          marginBottom: '0.5rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', flex: 1 }}>
          <button
            style={toolbarButtonStyle}
            onClick={() => setActiveTab('Makes')}
            disabled={activeTab === 'Makes'}
          >
            Makes
          </button>
          <button
            style={toolbarButtonStyle}
            onClick={() => setActiveTab('Models')}
            disabled={activeTab === 'Models'}
          >
            Models
          </button>
          {activeTab === 'Models' && (
            <button style={toolbarButtonStyle} onClick={() => setManageColumnsVisible(true)}>
              Manage Columns
            </button>
          )}
          {activeTab === 'Models' && (
            <button
              type="button"
              className="reset-filters-button"
              onClick={clearGridFilters}
              disabled={!gridApi}
              style={{
                ...toolbarButtonStyle,
                cursor: gridApi ? 'pointer' : 'not-allowed',
                opacity: gridApi ? 1 : 0.65
              }}
            >
              Reset Filters
            </button>
          )}
          {activeTab === 'Models' && (
            <input
              type="text"
              placeholder="Search models…"
              value={modelQuickFilterInput}
              onChange={(e) => onChangeModelQuickFilter(e.target.value)}
              style={{
                flex: 1,
                minWidth: 220,
                maxWidth: 420,
                height: '32px',
                padding: '0.4rem 0.6rem',
                borderRadius: 4,
                border: '1px solid #ccc'
              }}
            />
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginLeft: 'auto' }}>
          {activeTab === 'Models' && canWrite && (
            <button
              onClick={handleResequenceSortOrder}
              disabled={sortOrderResequencing}
              style={{
                ...toolbarButtonStyle,
                cursor: sortOrderResequencing ? 'not-allowed' : 'pointer',
                opacity: sortOrderResequencing ? 0.65 : 1
              }}
            >
              Re-space Sort Order
            </button>
          )}
          {activeTab === 'Models' && canWrite && (
            <button style={toolbarButtonStyle} onClick={openAddModal}>
              Add Model
            </button>
          )}
          {activeTab === 'Models' && (
            <>
              <GridSaveLayoutButton onSave={modelsLayout.saveLayout} style={toolbarButtonStyle} />
              <button style={toolbarButtonStyle} onClick={modelsLayout.resetToDefault}>
                ↻ Reset Columns
              </button>
            </>
          )}
          {activeTab === 'Makes' && canWrite && (
            <button style={toolbarButtonStyle} onClick={openAddMakeModal}>
              Add Make
            </button>
          )}
        </div>
      </div>

      {activeTab === 'Makes' ? (
        <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
            <thead style={{ backgroundColor: isDark ? '#333' : '#f0f0f0' }}>
              <tr>
                <th style={{ padding: '8px', border: '1px solid #ddd', color: isDark ? '#fff' : '#000' }}>MakeKey</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', color: isDark ? '#fff' : '#000' }}>Make</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', color: isDark ? '#fff' : '#000' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {makes.map((item) => (
                <tr key={item.MakeKey}>
                  <td style={{ padding: '8px', border: '1px solid #ddd', color: isDark ? '#fff' : '#000' }}>{item.MakeKey}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', color: isDark ? '#fff' : '#000' }}>{item.Make}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', color: isDark ? '#fff' : '#000' }}>
                    {canWrite && (
                      <>
                        <button onClick={() => handleEdit(item, 'make')} style={{ marginRight: '0.5rem', backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.3rem 0.6rem' }}>Edit</button>
                        <button onClick={() => handleDelete(item.MakeKey, 'make', item.Make)} style={{ marginRight: '0.5rem', backgroundColor: 'red', color: 'white', border: 'none', padding: '0.3rem 0.6rem' }}>Delete</button>
                        <button onClick={() => handleViewModels(item.Make)} style={{ backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.3rem 0.6rem' }}>View Models</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'} style={{ flex: 1, minHeight: 0, width: '100%' }}>
          <AgGridReact
            ref={gridRef}
            {...commonGridProps}
            rowData={mainRowData}
            onGridReady={onGridReadyMain}
            onColumnMoved={modelsLayout.onColumnChanged}
            onColumnResized={modelsLayout.onColumnResized}
            onColumnVisible={modelsLayout.onColumnChanged}
            onColumnPinned={modelsLayout.onColumnChanged}
          />
        </div>
      )}

      {/* Manage Columns Modal */}
      {manageColumnsVisible && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: isDark ? '#333' : '#fff', padding: '2rem', borderRadius: '8px', maxWidth: '400px', maxHeight: '80vh', overflowY: 'auto', color: isDark ? '#fff' : '#000' }}>
            <h3>Manage Columns</h3>
            {Object.keys(columnVisibility).map((field) => (
              <label key={field} style={{ display: 'block', marginBottom: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={columnVisibility[field]}
                  onChange={() => toggleColumn(field)}
                />
                {field.replace(/([A-Z])/g, ' $1').trim()}
              </label>
            ))}
            <button onClick={() => setManageColumnsVisible(false)} style={{ marginTop: '1rem', backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {/* Add Model Modal */}
      {addModalVisible && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: isDark ? '#333' : '#fff', padding: '2rem', borderRadius: '8px', width: '70%', maxHeight: '80vh', overflowY: 'auto', color: isDark ? '#fff' : '#000' }}>
            <h3>Add Model</h3>
            {errorPopupVisible && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: '#ff4d4f',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 1200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                maxWidth: '300px'
              }}>
                <span>{error}</span>
                <button
                  onClick={closeErrorPopup}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: '16px',
                    cursor: 'pointer',
                    marginLeft: '10px'
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <label>Make:</label>
              <select value={addFormData.Make || ''} onChange={(e) => handleInputChange('Make', e.target.value, 'add')} required style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.makes.map((opt) => (
                  <option key={opt.MakeKey} value={opt.Make}>
                    {opt.Make}
                  </option>
                ))}
              </select>
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Model Name:</label>
              <input
                type="text"
                value={addFormData.ModelName || ''}
                onChange={(e) => handleInputChange('ModelName', e.target.value, 'add')}
                required
                style={{ width: '100%', padding: '1rem', fontSize: '16px', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}
              />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Street Name:</label>
              <input type="text" value={addFormData.StreetName || ''} onChange={(e) => handleInputChange('StreetName', e.target.value, 'add')} style={{ width: '100%', padding: '0.75rem', fontSize: '14px', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Year:</label>
              <input type="number" value={addFormData.Year || ''} onChange={(e) => handleInputChange('Year', e.target.value, 'add')} required style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Sort Order:</label>
              <input type="number" value={addFormData.SortOrder || ''} onChange={(e) => handleInputChange('SortOrder', e.target.value, 'add')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Model ID:</label>
              <input type="text" value={addFormData.ModelID || ''} onChange={(e) => handleInputChange('ModelID', e.target.value, 'add')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Year ID:</label>
              <input type="text" value={addFormData.YearID || ''} onChange={(e) => handleInputChange('YearID', e.target.value, 'add')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Region:</label>
              <select value={addFormData.Region || ''} onChange={(e) => handleInputChange('Region', e.target.value, 'add')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.regions.map((opt) => (
                  <option key={opt.RegionName} value={opt.RegionName}>
                    {opt.RegionName}
                  </option>
                ))}
              </select>
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Engine Type:</label>
              <input type="text" value={addFormData.EngineType || ''} onChange={(e) => handleInputChange('EngineType', e.target.value, 'add')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Engine CC:</label>
              <input type="number" value={addFormData.EngineCC ?? ''} onChange={(e) => handleInputChange('EngineCC', e.target.value, 'add')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Vehicle Type:</label>
              <select value={addFormData.VehicleType || ''} onChange={(e) => handleInputChange('VehicleType', e.target.value, 'add')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.vehicleTypes.map((opt) => (
                  <option key={opt.VehicleTypeName} value={opt.VehicleTypeName}>
                    {opt.VehicleTypeName}
                  </option>
                ))}
              </select>
              <div style={{ marginTop: '1rem' }}>
                <button type="submit" disabled={loading} style={{ backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Save</button>
                <button type="button" onClick={() => setAddModalVisible(false)} style={{ marginLeft: '1rem', backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Make Modal */}
      {addMakeModalVisible && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: isDark ? '#333' : '#fff', padding: '2rem', borderRadius: '8px', width: '60%', maxHeight: '80vh', overflowY: 'auto', color: isDark ? '#fff' : '#000' }}>
            <h3>Add New Make</h3>
            {errorPopupVisible && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: '#ff4d4f',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 1200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                maxWidth: '300px'
              }}>
                <span>{error}</span>
                <button
                  onClick={closeErrorPopup}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: '16px',
                    cursor: 'pointer',
                    marginLeft: '10px'
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <label>Make:</label>
              <input
                type="text"
                value={addMakeFormData.Make || ''}
                onChange={(e) => handleInputChange('Make', e.target.value, 'addMake')}
                required
                style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}
              />
              <div style={{ marginTop: '1rem' }}>
                <button type="submit" disabled={loading} style={{ backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Add Make</button>
                <button type="button" onClick={() => setAddMakeModalVisible(false)} style={{ marginLeft: '1rem', backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Model Modal */}
      {editModalVisible && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: isDark ? '#333' : '#fff', padding: '2rem', borderRadius: '8px', width: '70%', maxHeight: '80vh', overflowY: 'auto', color: isDark ? '#fff' : '#000' }}>
            <h3>Edit Model</h3>
            {errorPopupVisible && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: '#ff4d4f',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 1200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                maxWidth: '300px'
              }}>
                <span>{error}</span>
                <button
                  onClick={closeErrorPopup}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: '16px',
                    cursor: 'pointer',
                    marginLeft: '10px'
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <label>Make:</label>
              <select value={formData.Make || ''} onChange={(e) => handleInputChange('Make', e.target.value, 'model')} required style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.makes.map((opt) => (
                  <option key={opt.MakeKey} value={opt.Make}>
                    {opt.Make}
                  </option>
                ))}
              </select>
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Model Name:</label>
              <input
                type="text"
                value={formData.ModelName || ''}
                onChange={(e) => handleInputChange('ModelName', e.target.value, 'model')}
                required
                style={{ width: '100%', padding: '1rem', fontSize: '16px', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}
              />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Street Name:</label>
              <input type="text" value={formData.StreetName || ''} onChange={(e) => handleInputChange('StreetName', e.target.value, 'model')} style={{ width: '100%', padding: '0.75rem', fontSize: '14px', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Year:</label>
              <input type="number" value={formData.Year || ''} onChange={(e) => handleInputChange('Year', e.target.value, 'model')} required style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Sort Order:</label>
              <input type="number" value={formData.SortOrder || ''} onChange={(e) => handleInputChange('SortOrder', e.target.value, 'model')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Model ID:</label>
              <input type="text" value={formData.ModelID || ''} onChange={(e) => handleInputChange('ModelID', e.target.value, 'model')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Year ID:</label>
              <input type="text" value={formData.YearID || ''} onChange={(e) => handleInputChange('YearID', e.target.value, 'model')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Region:</label>
              <select value={formData.Region || ''} onChange={(e) => handleInputChange('Region', e.target.value, 'model')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.regions.map((opt) => (
                  <option key={opt.RegionName} value={opt.RegionName}>
                    {opt.RegionName}
                  </option>
                ))}
              </select>
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Engine Type:</label>
              <input type="text" value={formData.EngineType || ''} onChange={(e) => handleInputChange('EngineType', e.target.value, 'model')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Engine CC:</label>
              <input type="number" value={formData.EngineCC ?? ''} onChange={(e) => handleInputChange('EngineCC', e.target.value, 'model')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Vehicle Type:</label>
              <select value={formData.VehicleType || ''} onChange={(e) => handleInputChange('VehicleType', e.target.value, 'model')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.vehicleTypes.map((opt) => (
                  <option key={opt.VehicleTypeName} value={opt.VehicleTypeName}>
                    {opt.VehicleTypeName}
                  </option>
                ))}
              </select>
              <div style={{ marginTop: '1rem' }}>
                <button type="submit" disabled={loading} style={{ backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Save Changes</button>
                <button type="button" onClick={() => { setEditModalVisible(false); setEditing(null); setFormData({}); }} style={{ marginLeft: '1rem', backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Copy Model Modal */}
      {copyModalVisible && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: isDark ? '#333' : '#fff', padding: '2rem', borderRadius: '8px', width: '70%', maxHeight: '80vh', overflowY: 'auto', color: isDark ? '#fff' : '#000' }}>
            <h3>Copy Model - Adjust Fields</h3>
            {errorPopupVisible && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: '#ff4d4f',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 1200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                maxWidth: '300px'
              }}>
                <span>{error}</span>
                <button
                  onClick={closeErrorPopup}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: '16px',
                    cursor: 'pointer',
                    marginLeft: '10px'
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <form onSubmit={handleSaveCopy}>
              <label>Make:</label>
              <select value={copyFormData.Make || ''} onChange={(e) => handleInputChange('Make', e.target.value, 'copy')} required style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.makes.map((opt) => (
                  <option key={opt.MakeKey} value={opt.Make}>
                    {opt.Make}
                  </option>
                ))}
              </select>
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Model Name:</label>
              <input
                type="text"
                value={copyFormData.ModelName || ''}
                onChange={(e) => handleInputChange('ModelName', e.target.value, 'copy')}
                required
                style={{ width: '100%', padding: '1rem', fontSize: '16px', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}
              />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Street Name:</label>
              <input type="text" value={copyFormData.StreetName || ''} onChange={(e) => handleInputChange('StreetName', e.target.value, 'copy')} style={{ width: '100%', padding: '0.75rem', fontSize: '14px', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Year:</label>
              <input type="number" value={copyFormData.Year || ''} onChange={(e) => handleInputChange('Year', e.target.value, 'copy')} required style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Sort Order:</label>
              <input type="number" value={copyFormData.SortOrder || ''} onChange={(e) => handleInputChange('SortOrder', e.target.value, 'copy')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Model ID:</label>
              <input type="text" value={copyFormData.ModelID || ''} onChange={(e) => handleInputChange('ModelID', e.target.value, 'copy')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Year ID:</label>
              <input type="text" value={copyFormData.YearID || ''} onChange={(e) => handleInputChange('YearID', e.target.value, 'copy')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Region:</label>
              <select value={copyFormData.Region || ''} onChange={(e) => handleInputChange('Region', e.target.value, 'copy')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.regions.map((opt) => (
                  <option key={opt.RegionName} value={opt.RegionName}>
                    {opt.RegionName}
                  </option>
                ))}
              </select>
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Engine Type:</label>
              <input type="text" value={copyFormData.EngineType || ''} onChange={(e) => handleInputChange('EngineType', e.target.value, 'copy')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Engine CC:</label>
              <input type="number" value={copyFormData.EngineCC ?? ''} onChange={(e) => handleInputChange('EngineCC', e.target.value, 'copy')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Vehicle Type:</label>
              <select value={copyFormData.VehicleType || ''} onChange={(e) => handleInputChange('VehicleType', e.target.value, 'copy')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.vehicleTypes.map((opt) => (
                  <option key={opt.VehicleTypeName} value={opt.VehicleTypeName}>
                    {opt.VehicleTypeName}
                  </option>
                ))}
              </select>
              <div style={{ marginTop: '1rem' }}>
                <button type="submit" disabled={loading} style={{ backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Save Copy</button>
                <button type="button" onClick={() => setCopyModalVisible(false)} style={{ marginLeft: '1rem', backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duplicate Year Modal */}
      {duplicateModalVisible && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: isDark ? '#333' : '#fff', padding: '2rem', borderRadius: '8px', width: '70%', maxHeight: '80vh', overflowY: 'auto', color: isDark ? '#fff' : '#000' }}>
            <h3>Duplicate Model - Adjust Fields</h3>
            {errorPopupVisible && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: '#ff4d4f',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 1200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                maxWidth: '300px'
              }}>
                <span>{error}</span>
                <button
                  onClick={closeErrorPopup}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: '16px',
                    cursor: 'pointer',
                    marginLeft: '10px'
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <form onSubmit={handleSaveDuplicate}>
              <label>Make:</label>
              <select value={duplicateFormData.Make || ''} onChange={(e) => handleInputChange('Make', e.target.value, 'duplicate')} required style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.makes.map((opt) => (
                  <option key={opt.MakeKey} value={opt.Make}>
                    {opt.Make}
                  </option>
                ))}
              </select>
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Model Name:</label>
              <input
                type="text"
                value={duplicateFormData.ModelName || ''}
                onChange={(e) => handleInputChange('ModelName', e.target.value, 'duplicate')}
                required
                style={{ width: '100%', padding: '1rem', fontSize: '16px', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}
              />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Street Name:</label>
              <input type="text" value={duplicateFormData.StreetName || ''} onChange={(e) => handleInputChange('StreetName', e.target.value, 'duplicate')} style={{ width: '100%', padding: '0.75rem', fontSize: '14px', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Year:</label>
              <input type="number" value={duplicateFormData.Year || ''} onChange={(e) => handleInputChange('Year', e.target.value, 'duplicate')} required style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Sort Order:</label>
              <input type="number" value={duplicateFormData.SortOrder || ''} onChange={(e) => handleInputChange('SortOrder', e.target.value, 'duplicate')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Model ID:</label>
              <input type="text" value={duplicateFormData.ModelID || ''} onChange={(e) => handleInputChange('ModelID', e.target.value, 'duplicate')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Year ID:</label>
              <input type="text" value={duplicateFormData.YearID || ''} onChange={(e) => handleInputChange('YearID', e.target.value, 'duplicate')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Region:</label>
              <select value={duplicateFormData.Region || ''} onChange={(e) => handleInputChange('Region', e.target.value, 'duplicate')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.regions.map((opt) => (
                  <option key={opt.RegionName} value={opt.RegionName}>
                    {opt.RegionName}
                  </option>
                ))}
              </select>
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Engine Type:</label>
              <input type="text" value={duplicateFormData.EngineType || ''} onChange={(e) => handleInputChange('EngineType', e.target.value, 'duplicate')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Engine CC:</label>
              <input type="number" value={duplicateFormData.EngineCC ?? ''} onChange={(e) => handleInputChange('EngineCC', e.target.value, 'duplicate')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }} />
              <label style={{ display: 'block', marginTop: '0.5rem' }}>Vehicle Type:</label>
              <select value={duplicateFormData.VehicleType || ''} onChange={(e) => handleInputChange('VehicleType', e.target.value, 'duplicate')} style={{ width: '100%', padding: '0.5rem', backgroundColor: isDark ? '#444' : '#fff', color: isDark ? '#fff' : '#000', border: '1px solid #ccc' }}>
                <option value="">Select...</option>
                {lookups.vehicleTypes.map((opt) => (
                  <option key={opt.VehicleTypeName} value={opt.VehicleTypeName}>
                    {opt.VehicleTypeName}
                  </option>
                ))}
              </select>
              <div style={{ marginTop: '1rem' }}>
                <button type="submit" disabled={loading} style={{ backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Save Duplicate</button>
                <button type="button" onClick={() => setDuplicateModalVisible(false)} style={{ marginLeft: '1rem', backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Models Modal */}
      {viewModelsModalVisible && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: isDark ? '#333' : '#fff', padding: '2rem', borderRadius: '8px', width: '90%', maxHeight: '90vh', overflowY: 'auto', color: isDark ? '#fff' : '#000' }}>
            <h3>Models for {selectedMake}</h3>
            <div className={isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'} style={{ height: 'calc(100vh - 120px)', width: '100%' }}>
              <AgGridReact
                ref={modalGridRef}
                {...commonGridProps}
                rowData={modalRowData}
                onGridReady={onGridReadyModal}
              />
            </div>
            <button onClick={() => setViewModelsModalVisible(false)} style={{ marginTop: '1rem', backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

{partsListModalVisible && (
  <div className="parts-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
    <div className="parts-modal-content" style={{ backgroundColor: isDark ? '#333' : '#fff', padding: '2rem', borderRadius: '8px', width: '90%', maxHeight: '90vh', overflowY: 'auto', color: isDark ? '#fff' : '#000' }}>
      <h3>Parts List for Model</h3>
      <style>{`
        @media print {
          html, body {
            height: unset !important;
            min-height: unset !important;
            margin: 0 !important;
            padding: 0 !important;
            box-sizing: border-box !important;
          }
          body * {
            visibility: hidden;
          }
          #printable, #printable * {
            visibility: visible !important;
          }
          #printable {
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          .parts-modal-overlay {
            position: static !important;
            top: auto !important;
            left: auto !important;
            right: auto !important;
            bottom: auto !important;
            background-color: transparent !important;
            align-items: flex-start !important;
            justifyContent: center !important;
            z-index: auto !important;
            height: unset !important;
            overflow: visible !important;
            display: block !important;
          }
          .parts-modal-content {
            background-color: white !important;
            padding: 0 !important;
            border-radius: 0 !important;
            width: 100% !important;
            max-width: none !important;
            max-height: none !important;
            min-height: 0 !important;
            overflow: visible !important;
            color: black !important;
            margin: 0 auto !important;
            box-sizing: border-box !important;
          }
          h3, .print-buttons {
            display: none !important;
          }
          .model-header { 
            display: none !important;
          }
          .print-only-header {
            position: running(modelHeader);
            text-align: center;
            margin: 0 !important;
            padding: 0 !important;
          }
          .print-only-header h2 { 
            font-weight: bold; 
            font-size: 7pt;
            margin: 0 !important; 
            padding: 0 !important;
          }
          .print-only-header p { 
            font-size: 4pt;
            margin: 1px 0 !important; 
            padding: 0 !important;
          }
          .print-only-header .header-label { 
            font-weight: bold;
          }
          .print-dummy-content {
            height: 1px !important;
            margin: 0 !important;
            padding: 0 !important;
            visibility: visible !important;
          }
          .print-table-wrapper {
            break-before: avoid !important;
            break-after: avoid !important;
            height: auto !important;
            overflow: visible !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .parts-table { 
            position: static !important;
            left: 0 !important;
            top: 0 !important;
            display: table !important; 
            width: 100% !important;
            max-width: none !important;
            border-collapse: collapse !important; 
            font-family: Arial, sans-serif; 
            font-size: 8pt;
            content-visibility: auto !important;
            will-change: contents !important;
            break-inside: auto !important;
            break-before: avoid !important;
            height: auto !important;
            min-height: 20px !important;
            margin: 0 !important;
            padding: 0 !important;
            box-sizing: border-box !important;
            print-color-adjust: exact !important;
          }
          .parts-table th, .parts-table td { 
            border: 1px solid black !important; 
            padding: 3px !important;
            text-align: left; 
            vertical-align: top; 
            break-inside: avoid !important;
            word-wrap: break-word !important;
          }
          .parts-table th { 
            font-weight: bold; 
            background-color: #f0f0f0 !important; 
          }
          .parts-table thead { 
            display: table-header-group !important; 
          }
          .parts-table tbody { 
            display: table-row-group !important; 
          }
          .parts-table tr { 
            break-inside: avoid !important; 
            break-after: auto !important; 
          }
          .ag-theme-alpine, .ag-theme-alpine-dark { 
            display: none !important; 
          }
          @page {
            size: A4 portrait;
            margin: 0.1cm;
            @top-center {
              content: element(modelHeader);
              font-family: Arial, sans-serif;
              font-size: 7pt;
              color: black;
            }
            @bottom-center {
              content: "Page " counter(page) " of " counter(pages);
              font-size: 7pt;
            }
          }
          @page :first {
            margin: 0.1cm;
          }
        }
        @media screen {
          .parts-table {
            position: absolute !important;
            left: -9999px !important;
            top: -9999px !important;
            width: 100% !important;
          }
          .parts-modal-content .print-only-header {
            display: none !important;
            visibility: hidden !important;
          }
          .model-header {
            text-align: center; 
            margin: 0 0 10px 0;
            font-family: Arial, sans-serif; 
          }
          .model-header h2 { 
            font-weight: bold; 
            font-size: 18pt; 
            margin: 0; 
            padding: 0;
          }
          .model-header p { 
            font-size: 12pt; 
            margin: 5px 0; 
            padding: 0;
          }
          .model-header .header-label { 
            font-weight: bold;
          }
        }
        .ag-theme-alpine, .ag-theme-alpine-dark {
          font-family: Arial, sans-serif;
          font-size: 12px;
          --ag-header-background-color: ${isDark ? '#444' : '#f0f0f0'};
          --ag-border-color: ${isDark ? '#555' : '#ccc'};
          height: 400px;
          width: 100%;
        }
      `}</style>
      <div id="printable">
        {loadingParts ? (
          <div>Loading parts...</div>
        ) : (
          <>
            {partsData.length === 0 ? (
              <div>No parts data available for this model.</div>
            ) : (
              <>
                {/* Inline header for screen display */}
                {selectedModel && (
                  <div className="model-header">
                    <h2 style={{ fontWeight: 'bold', fontSize: '18pt' }}>
                      {`${selectedModel.Make} ${selectedModel.ModelName} ${selectedModel.StreetName || ''} ${selectedModel.Year} ${selectedModel.Region || ''}`}
                    </h2>
                    <p style={{ fontSize: '12pt' }}>
                      {[
                        selectedModel.ModelID && { label: 'Model ID', value: selectedModel.ModelID },
                        selectedModel.YearID && { label: 'Year ID', value: selectedModel.YearID },
                        selectedModel.EngineType && { label: 'Engine Type', value: selectedModel.EngineType },
                        selectedModel.VehicleType && { label: 'Vehicle Type', value: selectedModel.VehicleType }
                      ].filter(Boolean).map((item, index, array) => (
                        <span key={item.label}>
                          <span className="header-label">{item.label}</span>: {item.value}
                          {index < array.length - 1 && ', '}
                        </span>
                      ))}
                    </p>
                  </div>
                )}
                {/* Running header for print */}
                {selectedModel && (
                  <div id="modelHeader" className="print-only-header">
                    <h2 style={{ fontWeight: 'bold', fontSize: '7pt' }}>
                      {`${selectedModel.Make} ${selectedModel.ModelName} ${selectedModel.StreetName || ''} ${selectedModel.Year} ${selectedModel.Region || ''}`}
                    </h2>
                    <p style={{ fontSize: '4pt' }}>
                      {[
                        selectedModel.ModelID && { label: 'Model ID', value: selectedModel.ModelID },
                        selectedModel.YearID && { label: 'Year ID', value: selectedModel.YearID },
                        selectedModel.EngineType && { label: 'Engine Type', value: selectedModel.EngineType },
                        selectedModel.VehicleType && { label: 'Vehicle Type', value: selectedModel.VehicleType }
                      ].filter(Boolean).map((item, index, array) => (
                        <span key={item.label}>
                          <span className="header-label">{item.label}</span>: {item.value}
                          {index < array.length - 1 && ', '}
                        </span>
                      ))}
                    </p>
                  </div>
                )}
                {/* AG Grid for on-screen modal display */}
                <div className={isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'} style={{ height: '400px', width: '100%' }}>
                  <AgGridReact
                    theme="legacy"
                    rowData={partsData}
                    columnDefs={[
                      { headerName: 'Item Type', field: 'ItemType', minWidth: 120, valueFormatter: params => params.value || '-' },
                      { headerName: 'Fitment Location', field: 'FitmentLocation', minWidth: 150, valueFormatter: params => params.value || '-' },
                      { headerName: 'OEM Number', field: 'OEMNumber', minWidth: 120, valueFormatter: params => params.value || '-' },
                      { headerName: 'SKU', field: 'SKU', minWidth: 120, valueFormatter: params => params.value || '-' },
                      { headerName: 'Quantity', field: 'Quantity', minWidth: 100, valueFormatter: params => params.value || '-' },
                      { headerName: 'OEM Note', field: 'OEMNote', minWidth: 150, valueFormatter: params => params.value || '-' },
                      { headerName: 'Additional Fitment Info', field: 'AdditionalFitmentInfo', minWidth: 200, valueFormatter: params => params.value || '-' }
                    ]}
                    defaultColDef={{
                      resizable: true,
                      sortable: true,
                      filter: true
                    }}
                    domLayout="normal"
                  />
                </div>
                {/* Dummy content to force print rendering */}
                <div className="print-dummy-content"></div>
                {/* Hidden table for printing */}
                <div className="print-table-wrapper">
                  <table className="parts-table">
                    <thead>
                      <tr>
                        <th style={{ width: '15%' }}>Item Type</th>
                        <th style={{ width: '20%' }}>Fitment Location</th>
                        <th style={{ width: '15%' }}>OEM Number</th>
                        <th style={{ width: '15%' }}>SKU</th>
                        <th style={{ width: '10%' }}>Quantity</th>
                        <th style={{ width: '15%' }}>OEM Note</th>
                        <th style={{ width: '10%' }}>Additional Fitment Info</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partsData.length === 0 ? (
                        <tr>
                          <td colSpan="7">No parts available</td>
                        </tr>
                      ) : (
                        partsData.map((item, index) => (
                          <tr key={index}>
                            <td style={{ width: '15%' }}>{item.ItemType || '-'}</td>
                            <td style={{ width: '20%' }}>{item.FitmentLocation || '-'}</td>
                            <td style={{ width: '15%' }}>{item.OEMNumber || '-'}</td>
                            <td style={{ width: '15%' }}>{item.SKU || '-'}</td>
                            <td style={{ width: '10%' }}>{item.Quantity || '-'}</td>
                            <td style={{ width: '15%' }}>{item.OEMNote || '-'}</td>
                            <td style={{ width: '10%' }}>{item.AdditionalFitmentInfo || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className="print-buttons" style={{ marginTop: '1rem' }}>
        <button onClick={handlePrint} style={{ backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Print</button>
        <button onClick={() => setPartsListModalVisible(false)} style={{ marginLeft: '1rem', backgroundColor: isDark ? '#555' : '#f0f0f0', color: isDark ? '#fff' : '#000', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  </div>
)}

      {modelOverviewModal && (
        <DataCollectionOverviewByModel
          modelKey={modelOverviewModal.modelKey}
          onClose={closeModelOverview}
          permissions={permissions}
          isDark={isDark}
          parentGroupKey={modelOverviewModal.parentGroupKey}
          subGroupKey={modelOverviewModal.subGroupKey}
          label={modelOverviewModal.label}
          onRequestYearChange={(targetMeta) => {
            const modelKey = targetMeta?.modelKey ?? targetMeta?.ModelKey;
            if (!modelKey) {
              window.alert('No matching year found for this model.');
              return;
            }

            setModelOverviewModal((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                modelKey,
                label: formatModelLabel(targetMeta, prev.label)
              };
            });
          }}
        />
      )}

      {loading && <div>Loading...</div>}
    </div>
  );
}